import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Code2,
  ExternalLink,
  Globe2,
  ImagePlus,
  MessageCircleMore,
  PackageCheck,
  QrCode,
  Rocket,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  TrendingUp,
  WandSparkles,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChatDemoSection } from "../components/landing/ChatDemoSection";
import { DashboardDemoSection } from "../components/landing/DashboardDemoSection";
import { ExportDemoSection } from "../components/landing/ExportDemoSection";
import { LandingNavbar } from "../components/landing/LandingNavbar";
import { Brand } from "../components/ui/Brand";
import { slideDeckUrl } from "../lib/links";

type RevealVariant = "up" | "left" | "right" | "scale";

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  variant?: RevealVariant;
};

function Reveal({ children, className = "", delay = 0, variant = "up" }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.12);
      },
      { threshold: [0, 0.12, 0.35], rootMargin: "-4% 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`scroll-reveal scroll-reveal--${variant} ${visible ? "is-visible" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </div>
  );
}

type AnimatedCounterProps = {
  value: number;
  rangeStart?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
};

function AnimatedCounter({ value, rangeStart, decimals = 0, prefix = "", suffix = "", duration = 1450 }: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const activeRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const stopAnimation = () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };

    const runAnimation = () => {
      stopAnimation();
      if (prefersReducedMotion) {
        setDisplayValue(value);
        return;
      }

      const startedAt = performance.now();
      setDisplayValue(0);

      const tick = (now: number) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        setDisplayValue(value * eased);
        if (progress < 1) frameRef.current = window.requestAnimationFrame(tick);
      };

      frameRef.current = window.requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.45 && !activeRef.current) {
          activeRef.current = true;
          runAnimation();
        } else if (entry.intersectionRatio < 0.1 && activeRef.current) {
          activeRef.current = false;
          if (!prefersReducedMotion) {
            stopAnimation();
            setDisplayValue(0);
          }
        }
      },
      { threshold: [0, 0.1, 0.45, 0.8], rootMargin: "-6% 0px -8% 0px" },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      stopAnimation();
    };
  }, [duration, value]);

  const numberFormatter = new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  const formatted = numberFormatter.format(displayValue);
  const rangeStartValue = rangeStart === undefined || value === 0 ? undefined : rangeStart * (displayValue / value);
  const formattedRangeStart = rangeStartValue === undefined ? undefined : numberFormatter.format(rangeStartValue);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{formattedRangeStart !== undefined ? `${formattedRangeStart}-` : ""}{formatted}{suffix}
    </span>
  );
}

const benefits = [
  { icon: Code2, label: "Tanpa coding" },
  { icon: Zap, label: "Siap dalam hitungan menit" },
  { icon: QrCode, label: "Pakai QRIS sendiri" },
];

type ProblemMetric = {
  value: number;
  rangeStart?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  label: string;
  note: string;
  sourceLabel: string;
  sourceUrl: string;
  tone: "red" | "orange" | "slate" | "green";
  icon: LucideIcon;
};

const problemMetrics: ProblemMetric[] = [
  {
    value: 18,
    rangeStart: 10,
    suffix: "%",
    label: "Biaya admin seller",
    note: "Kisaran biaya marketplace dapat berada di sekitar 10–18% tergantung kategori, status penjual, dan program yang diikuti.",
    sourceLabel: "Ketentuan Shopee",
    sourceUrl: "https://help.shopee.co.id/portal/4/article/71187",
    tone: "red",
    icon: CircleDollarSign,
  },
  {
    value: 1250,
    prefix: "Rp",
    label: "Biaya proses tiap pesanan",
    note: "Contoh biaya proses yang dikenakan per transaksi selesai, di luar biaya admin dan program lain.",
    sourceLabel: "Ketentuan Shopee",
    sourceUrl: "https://help.shopee.co.id/portal/4/article/71187",
    tone: "orange",
    icon: ShoppingBag,
  },
  {
    value: 20,
    rangeStart: 1.5,
    decimals: 1,
    prefix: "Rp",
    suffix: " juta",
    label: "Biaya website profesional",
    note: "Kisaran biaya pembuatan website di pasar Indonesia; nilai aktual bergantung desain, halaman, dan fitur yang dibutuhkan.",
    sourceLabel: "Estimasi pasar 2026",
    sourceUrl: "https://iniwebsitemu.com/artikel/harga-jasa-pembuatan-website-company-profile",
    tone: "slate",
    icon: Clock3,
  },
  {
    value: 60.5,
    decimals: 1,
    suffix: "%",
    label: "Kontribusi UMKM ke PDB",
    note: "Besarnya peran UMKM menunjukkan bahwa akses digital yang lebih sederhana dapat memberi dampak luas.",
    sourceLabel: "Bank Indonesia",
    sourceUrl: "https://www.bi.go.id/id/publikasi/ruang-media/news-release/Pages/sp_2812726.aspx",
    tone: "green",
    icon: TrendingUp,
  },
];

const steps = [
  {
    icon: MessageCircleMore,
    title: "Chat di WhatsApp",
    text: "Kirim #CSJW untuk memulai.",
  },
  {
    icon: ImagePlus,
    title: "Kirim data usaha",
    text: "Foto, produk, harga, dan QRIS.",
  },
  {
    icon: WandSparkles,
    title: "Kami buatkan",
    text: "Tampilan rapi disiapkan untukmu.",
  },
  {
    icon: Rocket,
    title: "Langsung online",
    text: "Bagikan link dan mulai menerima pesan.",
  },
];

const examples = [
  {
    name: "Sambal Bu Rina",
    category: "Kuliner pedas",
    image: "/images/showcase-v16/site-sambal-bu-rina.webp",
    title: "Sambal cumi yang langsung bikin lapar",
    accent: "bg-[#9e241f]",
  },
  {
    name: "Kopi Nusa",
    category: "Kopi & minuman",
    image: "/images/showcase-v16/site-kopi-nusa.webp",
    title: "Kopi lokal dengan rasa premium",
    accent: "bg-[#3c2b1f]",
  },
  {
    name: "Dapoer Kriuk",
    category: "Camilan rumahan",
    image: "/images/showcase-v16/site-dapoer-kriuk.webp",
    title: "Camilan renyah, tampil makin meyakinkan",
    accent: "bg-[#b86b22]",
  },
  {
    name: "Batik Lestari",
    category: "Fashion & kerajinan",
    image: "/images/showcase-v16/site-batik-lestari.webp",
    title: "Warisan budaya dengan etalase berkelas",
    accent: "bg-[#8b5326]",
  },
];

const metrics = [
  { value: "0%", label: "Potongan transaksi", icon: CircleDollarSign },
  { value: "24/7", label: "Website selalu online", icon: Globe2 },
  { value: "100%", label: "QRIS tetap milikmu", icon: ShieldCheck },
  { value: "1 chat", label: "Untuk mulai dan update", icon: MessageCircleMore },
];

const packages = [
  {
    name: "Siap Digital",
    description: "Mulai jualan online tanpa biaya bulanan.",
    price: "Gratis",
    suffix: "selamanya",
    features: ["1 website dan 1 produk", "Subdomain csjw.pages.dev", "Pesan langsung ke WhatsApp", "QRIS milik UMKM"],
    href: "/siap-digital",
    cta: "Lihat Siap Digital",
    tone: "green",
  },
  {
    name: "Siap Profesional",
    description: "Untuk usaha yang ingin tampil lebih serius.",
    price: "Rp49.000",
    suffix: "/ bulan · pilot",
    features: ["Semua fitur Siap Digital", "Dashboard dan builder visual", "Banyak produk dan laporan", "Domain sendiri"],
    href: "/siap-profesional",
    cta: "Lihat Siap Profesional",
    tone: "featured",
  },
  {
    name: "Siap Export",
    description: "Tampil meyakinkan di pasar global.",
    price: "Rp149.000",
    suffix: "/ bulan · pilot",
    features: ["Semua fitur Profesional", "Profil dua bahasa", "Peta peluang pasar", "Katalog dan buyer tools"],
    href: "/siap-export",
    cta: "Lihat Siap Export",
    tone: "orange",
  },
];

const faqs = [
  {
    question: "Apakah saya harus bisa membuat website?",
    answer: "Tidak. Kamu cukup mengirim data usaha melalui WhatsApp. CSJW membantu menyusun tampilannya agar siap dibagikan.",
  },
  {
    question: "Kapan link website dibuat?",
    answer: "Link baru dibuat setelah nama usaha, produk, harga, foto produk, dan QRIS sudah lengkap. Jadi tidak ada preview kosong.",
  },
  {
    question: "Apakah pembayaran masuk ke CSJW?",
    answer: "Tidak untuk paket Siap Digital. Pembeli membayar langsung ke QRIS milik UMKM dan konfirmasi melalui WhatsApp.",
  },
  {
    question: "Bisakah website diubah setelah jadi?",
    answer: "Bisa. Paket gratis dapat diperbarui melalui chat. Paket Profesional dan Export juga memiliki dashboard serta builder visual.",
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <article className="border-b border-slate-200 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-5 py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-base font-black tracking-[-.02em] text-slate-900 sm:text-lg">{question}</span>
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 transition ${open ? "rotate-180 bg-brand-50 text-brand-700" : "bg-white text-slate-500"}`}>
          <ChevronDown size={18} />
        </span>
      </button>
      <div className={`grid transition-[grid-template-rows,opacity] duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <p className="max-w-3xl pb-5 text-sm leading-7 text-slate-500">{answer}</p>
        </div>
      </div>
    </article>
  );
}

function PackageCard({ plan, index }: { plan: (typeof packages)[number]; index: number }) {
  const featured = plan.tone === "featured";
  const orange = plan.tone === "orange";

  return (
    <Reveal delay={index * 110} className="h-full">
      <article
        className={`relative flex h-full flex-col rounded-[1.8rem] border bg-white p-6 transition duration-300 hover:-translate-y-2 hover:shadow-[0_28px_80px_rgba(25,67,41,.12)] ${
          featured
            ? "border-brand-400 shadow-[0_24px_70px_rgba(13,148,77,.14)] ring-4 ring-brand-50"
            : orange
              ? "border-accent-300"
              : "border-slate-200"
        }`}
      >
        {featured && (
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-4 py-1.5 text-[10px] font-black uppercase tracking-[.14em] text-white shadow-lg">
            Paling populer
          </span>
        )}
        <p className={`text-sm font-black ${orange ? "text-accent-600" : "text-slate-900"}`}>{plan.name}</p>
        <p className="mt-2 min-h-11 text-sm leading-6 text-slate-500">{plan.description}</p>
        <div className="mt-5 flex items-end gap-2">
          <strong className={`text-4xl font-black tracking-[-.06em] ${orange ? "text-accent-600" : "text-slate-950"}`}>{plan.price}</strong>
          <span className="pb-1 text-xs font-bold text-slate-400">{plan.suffix}</span>
        </div>
        <ul className="mt-6 flex-1 space-y-3">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3 text-sm leading-5 text-slate-600">
              <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${orange ? "bg-accent-100 text-accent-700" : "bg-brand-100 text-brand-700"}`}>
                <Check size={13} strokeWidth={3} />
              </span>
              {feature}
            </li>
          ))}
        </ul>
        <div className="mt-7">
          <Link
            to={plan.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-black transition hover:-translate-y-0.5 ${
              orange
                ? "bg-accent-500 text-white shadow-lg shadow-accent-500/20 hover:bg-accent-600"
                : "bg-brand-600 text-white shadow-lg shadow-brand-600/20 hover:bg-brand-700"
            }`}
          >
            {plan.cta} <ArrowRight size={15} />
          </Link>
        </div>
      </article>
    </Reveal>
  );
}

export function LandingPage() {
  const [activeExample, setActiveExample] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 0);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveExample((current) => (current + 1) % examples.length);
    }, 4400);
    return () => window.clearInterval(timer);
  }, []);

  const showPrevious = () => setActiveExample((current) => (current - 1 + examples.length) % examples.length);
  const showNext = () => setActiveExample((current) => (current + 1) % examples.length);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#fbfcfa] text-slate-900">
      <div className="fixed inset-x-0 top-0 z-[80] h-1 origin-left bg-gradient-to-r from-brand-500 via-brand-600 to-accent-500" style={{ transform: `scaleX(${scrollProgress})` }} aria-hidden="true" />
      <LandingNavbar />

      <main>
        <section id="beranda" className="relative overflow-hidden pb-16 pt-32 sm:pb-20 sm:pt-36 lg:pt-40">
          <div className="pointer-events-none absolute inset-0">
            <div className="hero-orb hero-orb--green" />
            <div className="hero-orb hero-orb--orange" />
            <div className="soft-grid absolute inset-x-0 top-0 h-[680px] opacity-60" />
          </div>

          <div className="app-container relative grid items-center gap-12 lg:grid-cols-[.92fr_1.08fr] lg:gap-10 xl:gap-16">
            <Reveal variant="left" className="relative z-10">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/80 px-3 py-2 text-xs font-black text-brand-700 shadow-sm backdrop-blur-xl">
                <Sparkles size={15} /> Nggak Perlu Jago Teknologi untuk Punya Website Profesional.
              </span>
              <h1 className="mt-6 max-w-[760px] text-[3.15rem] font-black leading-[.98] tracking-[-.065em] text-slate-950 sm:text-[4rem] lg:text-[4.6rem] xl:text-[5.2rem]">
                <span className="text-brand-600">Bikin Website</span><br />Tanpa Coding<br />Cukup Chating
              </h1>
              <p className="mt-6 max-w-xl text-base leading-8 text-slate-600 sm:text-lg">
                Kirim data usaha lewat WhatsApp. CSJW mengubahnya menjadi website yang siap dibagikan, menerima pesanan, dan membangun kepercayaan pelanggan.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#demo-chat" className="btn-secondary !rounded-xl !px-6 !py-3.5 !text-base">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-950 text-white"><ArrowRight size={14} /></span>
                  Lihat Demo
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                {benefits.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-sm font-bold text-slate-600">
                    <Icon size={17} className="text-brand-600" /> {label}
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal variant="right" delay={120} className="relative">
              <div className="relative mx-auto max-w-[720px]">
                <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-brand-100/80 via-white to-accent-100/70 blur-2xl" />
                <div className="hero-media relative overflow-hidden rounded-[2.2rem] border border-white bg-white p-2 shadow-[0_40px_120px_rgba(25,73,44,.18)]">
                  <img
                    src="/images/hero-chat-to-web.webp"
                    alt="Pelaku UMKM menggunakan CSJW untuk menyiapkan website jualan"
                    className="aspect-[16/11] w-full rounded-[1.8rem] object-cover object-center"
                  />
                  <div className="absolute inset-x-2 bottom-2 h-36 rounded-b-[1.8rem] bg-gradient-to-t from-emerald-950/65 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 text-white">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[.18em] text-brand-200">Website siap dibagikan</p>
                      <p className="mt-1 text-xl font-black">Sambal Bu Rina</p>
                    </div>
                    <span className="rounded-full bg-white px-4 py-2 text-xs font-black text-brand-700 shadow-xl">Lihat website</span>
                  </div>
                </div>

                <div className="motion-float absolute -left-4 top-6 hidden w-[205px] rounded-2xl border border-white/80 bg-white/92 p-4 shadow-[0_18px_55px_rgba(31,73,47,.15)] backdrop-blur-xl sm:block lg:-left-8">
                  <div className="flex items-center justify-between"><span className="text-xs font-black text-brand-700">#CSJW</span><CheckCircle2 size={17} className="text-brand-600" /></div>
                  <p className="mt-2 text-xs font-bold leading-5 text-slate-600">Data usahamu sudah lengkap.</p>
                </div>

                <div className="motion-float motion-float--delay absolute -right-4 top-14 rounded-2xl border border-white/80 bg-white/94 p-3.5 shadow-[0_18px_55px_rgba(31,73,47,.14)] backdrop-blur-xl sm:right-2 lg:-right-8">
                  <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-50 text-brand-700"><ShieldCheck size={18} /></span><div><p className="text-xs font-black text-slate-900">Lebih hemat</p><p className="text-[10px] text-slate-500">Tanpa potongan marketplace</p></div></div>
                </div>

                <div className="motion-float motion-float--slow absolute -right-1 bottom-24 hidden rounded-2xl border border-white/80 bg-white/94 p-3.5 shadow-[0_18px_55px_rgba(31,73,47,.14)] backdrop-blur-xl sm:block lg:-right-10">
                  <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl bg-accent-50 text-accent-600"><QrCode size={18} /></span><div><p className="text-xs font-black text-slate-900">Pakai QRIS sendiri</p><p className="text-[10px] text-slate-500">Keuntungan tetap milikmu</p></div></div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="masalah" className="app-container py-10 sm:py-14">
          <Reveal className="text-center">
            <span className="eyebrow">Sebelum dan sesudah</span>
            <h2 className="section-title mt-5">Dari ribet jadi siap jualan.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">Lihat perubahan saat UMKM memiliki website sendiri yang mudah dikelola.</p>
          </Reveal>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <Reveal variant="left">
              <article className="group overflow-hidden rounded-[2rem] border border-red-100 bg-white shadow-[0_24px_75px_rgba(93,45,35,.10)]">
                <div className="overflow-hidden bg-[#fff7f5]">
                  <img
                    src="/images/landing/before-challenges.webp"
                    alt="Tantangan UMKM sebelum memiliki website sendiri"
                    width={1448}
                    height={1086}
                    className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-[1.025]"
                  />
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-red-100 px-5 py-4 sm:px-6">
                  <div><p className="text-sm font-black text-slate-900">Margin tertekan, update terasa rumit</p><p className="mt-1 text-xs text-slate-500">Banyak kanal, banyak biaya, sedikit kendali.</p></div>
                  <span className="rounded-full bg-red-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.14em] text-red-600">Sebelumnya</span>
                </div>
              </article>
            </Reveal>

            <Reveal variant="right" delay={100}>
              <article className="group overflow-hidden rounded-[2rem] border border-brand-100 bg-white shadow-[0_24px_75px_rgba(25,93,55,.12)]">
                <div className="overflow-hidden bg-[#f4fbf6]">
                  <img
                    src="/images/landing/after-csjw.webp"
                    alt="UMKM tampil lebih profesional setelah menggunakan CSJW"
                    width={1448}
                    height={1086}
                    className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-[1.025]"
                  />
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-brand-100 px-5 py-4 sm:px-6">
                  <div><p className="text-sm font-black text-slate-900">Website sendiri, usaha makin dipercaya</p><p className="mt-1 text-xs text-slate-500">Update lebih mudah dan transaksi tetap milikmu.</p></div>
                  <span className="rounded-full bg-brand-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.14em] text-brand-700">Dengan CSJW</span>
                </div>
              </article>
            </Reveal>
          </div>
        </section>

        <section id="data-masalah" className="relative overflow-hidden py-20 sm:py-24">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-emerald-950 to-brand-900" />
          <div className="soft-grid absolute inset-0 opacity-10" />
          <div className="app-container relative">
            <Reveal className="text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[.16em] text-brand-100">
                <TrendingUp size={15} /> Angka yang perlu dilihat
              </span>
              <h2 className="mx-auto mt-5 max-w-4xl text-3xl font-black tracking-[-.045em] text-white sm:text-4xl lg:text-5xl">Biaya kecil yang terus berulang bisa menggerus margin.</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-white/65">CSJW dirancang untuk memberi UMKM jalur yang lebih sederhana menuju website milik sendiri.</p>
            </Reveal>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {problemMetrics.map(({ value, rangeStart, decimals, prefix, suffix, label, note, sourceLabel, sourceUrl, tone, icon: Icon }, index) => {
                const toneClass = tone === "red"
                  ? "from-red-500/20 to-red-400/5 text-red-200"
                  : tone === "orange"
                    ? "from-accent-500/20 to-accent-400/5 text-orange-200"
                    : tone === "green"
                      ? "from-brand-400/20 to-brand-300/5 text-emerald-200"
                      : "from-slate-400/20 to-slate-300/5 text-slate-200";

                return (
                  <Reveal key={label} delay={index * 110} variant="up" className="h-full">
                    <article className={`group flex h-full flex-col rounded-[1.7rem] border border-white/10 bg-gradient-to-br ${toneClass} p-6 backdrop-blur-sm transition duration-300 hover:-translate-y-2 hover:border-white/25 hover:bg-white/[.08]`}>
                      <span className="grid h-12 w-12 place-items-center rounded-2xl border border-white/10 bg-white/10 text-white"><Icon size={23} /></span>
                      <p className="mt-6 text-4xl font-black tracking-[-.055em] text-white sm:text-5xl">
                        <AnimatedCounter value={value} rangeStart={rangeStart} decimals={decimals} prefix={prefix} suffix={suffix} duration={1450 + index * 120} />
                      </p>
                      <h3 className="mt-3 text-base font-black text-white">{label}</h3>
                      <p className="mt-3 flex-1 text-sm leading-6 text-white/60">{note}</p>
                      <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-xs font-black text-white/70 hover:text-white">
                        {sourceLabel} <ExternalLink size={13} />
                      </a>
                    </article>
                  </Reveal>
                );
              })}
            </div>

          </div>
        </section>

        <section id="cara-kerja" className="app-container py-20 sm:py-24">
          <Reveal className="text-center">
            <span className="eyebrow">Mudah dari awal</span>
            <h2 className="section-title mt-5">Cara kerja CSJW</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">Empat langkah sederhana untuk membawa produkmu online.</p>
          </Reveal>

          <div className="relative mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <Reveal variant="scale" className="absolute left-[10%] right-[10%] top-14 hidden h-px overflow-hidden bg-slate-200 lg:block">
              <span className="step-line block h-full w-full bg-gradient-to-r from-brand-500 via-brand-400 to-accent-400" />
            </Reveal>
            {steps.map(({ icon: Icon, title, text }, index) => (
              <Reveal key={title} delay={index * 110} className="relative">
                <article className="group flex h-full flex-col items-center rounded-[1.7rem] border border-slate-200 bg-white p-6 text-center shadow-[0_16px_50px_rgba(28,63,41,.06)] transition hover:-translate-y-2 hover:border-brand-200 hover:shadow-[0_25px_70px_rgba(28,63,41,.12)]">
                  <span className="absolute left-5 top-5 grid h-7 w-7 place-items-center rounded-full bg-brand-600 text-xs font-black text-white">{index + 1}</span>
                  <span className="relative z-10 grid h-20 w-20 place-items-center rounded-full border border-slate-200 bg-white text-brand-600 shadow-lg shadow-slate-200/50 transition group-hover:scale-110 group-hover:border-brand-200 group-hover:bg-brand-50">
                    <Icon size={31} strokeWidth={2.2} />
                  </span>
                  <h3 className="mt-5 text-lg font-black text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <ChatDemoSection />

        <DashboardDemoSection />

        <ExportDemoSection />

        <section id="contoh" className="relative overflow-hidden bg-white py-20 sm:py-24">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-200 to-transparent" />
          <div className="app-container">
            <Reveal className="text-center">
              <span className="eyebrow">Contoh website</span>
              <h2 className="section-title mt-5">Produkmu layak tampil lebih menarik.</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">Pilih gaya yang cocok. CSJW membantu menyesuaikannya dengan usaha dan produkmu.</p>
            </Reveal>

            <div className="relative mt-12">
              <button type="button" onClick={showPrevious} aria-label="Contoh sebelumnya" className="absolute -left-3 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-xl hover:-translate-x-1 hover:text-brand-700 sm:-left-5">
                <ArrowLeft size={19} />
              </button>
              <button type="button" onClick={showNext} aria-label="Contoh berikutnya" className="absolute -right-3 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-xl hover:translate-x-1 hover:text-brand-700 sm:-right-5">
                <ArrowRight size={19} />
              </button>

              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                {examples.map((site, index) => {
                  const active = activeExample === index;
                  return (
                    <Reveal key={site.name} delay={index * 90}>
                      <button
                        type="button"
                        onClick={() => setActiveExample(index)}
                        className={`group w-full overflow-hidden rounded-[1.55rem] border bg-white text-left transition duration-500 ${active ? "-translate-y-3 border-brand-300 shadow-[0_28px_80px_rgba(25,93,55,.18)] ring-4 ring-brand-50" : "border-slate-200 shadow-[0_16px_50px_rgba(25,54,37,.07)] hover:-translate-y-2 hover:border-brand-200"}`}
                      >
                        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                          <div><p className="text-xs font-black text-slate-900">{site.name}</p><p className="mt-0.5 text-[10px] text-slate-400">{site.category}</p></div>
                          <span className={`h-2.5 w-2.5 rounded-full transition ${active ? "bg-brand-500" : "bg-slate-200"}`} />
                        </div>
                        <div className="aspect-[3/4] overflow-hidden bg-slate-100">
                          <img
                            src={site.image}
                            alt={`Contoh website ${site.name}`}
                            width={1086}
                            height={1448}
                            loading="lazy"
                            className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-[1.025]"
                          />
                        </div>
                        <div className="p-4">
                          <p className="text-lg font-black leading-snug text-slate-900">{site.title}</p>
                        </div>
                      </button>
                    </Reveal>
                  );
                })}
              </div>

              <div className="mt-8 flex justify-center gap-2">
                {examples.map((site, index) => (
                  <button key={site.name} type="button" onClick={() => setActiveExample(index)} aria-label={`Pilih ${site.name}`} className={`h-2.5 rounded-full transition-all ${activeExample === index ? "w-8 bg-brand-600" : "w-2.5 bg-slate-200 hover:bg-slate-300"}`} />
                ))}
              </div>

            </div>
          </div>
        </section>

        <section className="app-container py-14">
          <Reveal variant="scale">
            <div className="grid gap-0 overflow-hidden rounded-[1.8rem] border border-slate-200 bg-white shadow-[0_25px_80px_rgba(28,63,41,.08)] sm:grid-cols-2 lg:grid-cols-4">
              {metrics.map(({ value, label, icon: Icon }, index) => (
                <div key={label} className={`flex items-center gap-4 p-6 ${index !== metrics.length - 1 ? "border-b border-slate-100 sm:border-b-0 sm:border-r" : ""}`}>
                  <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${index % 2 === 0 ? "bg-accent-50 text-accent-600" : "bg-brand-50 text-brand-700"}`}><Icon size={23} /></span>
                  <div><p className="text-2xl font-black tracking-[-.04em] text-slate-950">{value}</p><p className="mt-1 text-xs font-bold text-slate-500">{label}</p></div>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <section id="paket" className="app-container py-20 sm:py-24">
          <Reveal className="text-center">
            <span className="eyebrow">Pilih paket</span>
            <h2 className="section-title mt-5">Paket yang pas untuk bisnismu.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">Mulai gratis, lalu naik kelas ketika kebutuhan usahamu berkembang.</p>
          </Reveal>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {packages.map((plan, index) => <PackageCard key={plan.name} plan={plan} index={index} />)}
          </div>
        </section>

        <section className="app-container py-10 sm:py-14">
          <Reveal variant="scale">
            <div className="relative overflow-hidden rounded-[2.2rem] bg-gradient-to-br from-emerald-950 via-brand-900 to-brand-700 p-8 text-white shadow-[0_36px_110px_rgba(7,57,34,.25)] sm:p-12">
              <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full border border-white/10" />
              <div className="absolute -right-2 -top-4 h-44 w-44 rounded-full border border-white/10" />
              <div className="relative grid items-center gap-10 lg:grid-cols-[1fr_auto]">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[.16em] text-brand-100"><PackageCheck size={15} /> Kenali CSJW</span>
                  <h2 className="mt-5 max-w-3xl text-4xl font-black tracking-[-.05em] sm:text-5xl">Lihat bagaimana CSJW membantu UMKM naik kelas.</h2>
                  <p className="mt-4 max-w-2xl text-base leading-8 text-white/65">Pelajari alur layanan, pilihan paket, fitur utama, dan rencana pengembangan CSJW melalui slide deck.</p>
                </div>
                <a href={slideDeckUrl} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-accent-500 px-6 py-4 text-base font-black text-white shadow-xl shadow-accent-950/20 hover:-translate-y-1 hover:bg-accent-400">
                  <ExternalLink size={20} /> Lihat Slide Deck <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </Reveal>
        </section>

        <section id="faq" className="app-container py-20 sm:py-24">
          <div className="grid gap-12 lg:grid-cols-[.7fr_1.3fr]">
            <Reveal variant="left">
              <span className="eyebrow">Pertanyaan umum</span>
              <h2 className="section-title mt-5">Sebelum mulai, ini jawabannya.</h2>
              <p className="mt-4 max-w-md text-base leading-8 text-slate-500">Hal-hal penting yang paling sering ditanyakan oleh pemilik usaha.</p>
            </Reveal>
            <Reveal variant="right" delay={100} className="rounded-[1.8rem] border border-slate-200 bg-white px-6 shadow-[0_20px_65px_rgba(28,63,41,.07)] sm:px-8">
              {faqs.map((item) => <FAQItem key={item.question} {...item} />)}
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="app-container pb-10 pt-4">
        <div className="flex flex-col gap-6 border-t border-slate-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <Brand />
          <p className="text-xs text-slate-400">© 2026 CSJW. Chat Sekali, Jadi Website.</p>
          <div className="flex flex-wrap gap-5 text-xs font-bold text-slate-500">
            <a href="#cara-kerja" className="hover:text-brand-700">Cara kerja</a>
            <a href="#dashboard" className="hover:text-brand-700">Dashboard</a>
            <a href="#demo-export" className="hover:text-brand-700">Siap Export</a>
            <a href="#contoh" className="hover:text-brand-700">Contoh</a>
            <a href="#paket" className="hover:text-brand-700">Paket</a>
            <Link to="/login" target="_blank" rel="noopener noreferrer" className="hover:text-brand-700">Masuk</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
